<template>
    <div class="mb-[100px]">
        <div class="flex mt-[42px] mb-[42px] justify-between h-[42px]">
            <h1
                class="text-[21px] font-medium leading-normal tracking-small text-gray-800"
            >
                <span class="flex flex-wrap items-center gap-2">
                    <span class="flex items-center gap-2">
                        <span
                            class="h-2 w-2 rounded-full"
                            :class="
                                reportedDomain.active
                                    ? 'bg-green-500'
                                    : 'bg-gray-400'
                            "
                        ></span>
                        <span class="text-gray-800">
                            {{ reportedDomain.domain }}
                        </span>
                    </span>
                </span>
            </h1>

            <div>
                <DateRangeField
                    :date-range="dateRange"
                    :reported-domain="reportedDomain"
                    :display-full-block="true"
                />
            </div>
        </div>

        <BasicTabs
            v-if="hasReportsAccess"
            :tabs="tabs"
            :tab-key="'domain-reports'"
        />

        <div v-if="!hasReportsAccess" class="mt-[42px]">
            <InfoCard
                :img-src="trialEndedImg"
                img-alt="Trial ended"
                :title="t('vue.your_trial_ended')"
                :body-text="t('vue.your_trial_ended_action_step')"
                :emphasis-text="t('vue.your_trial_ended_action_step_continue')"
                :button-label="t('vue.choose_dmarceye_plan')"
                :button-href="route('billing.subscriptions.index')"
            />
        </div>

        <div v-else>
            <InfoCard
                v-if="reports.total.pass === 0 && reports.total.fail === 0"
                :img-src="dataNotFound"
                img-alt="User has no data to display"
                :title="t('vue.waiting_for_reports')"
                :body-text="t('vue.no_results_for_domain')"
                :show-dot="false"
                :disable-top-margin="false"
            />

            <template v-else>
                <div class="mt-[22px]">
                    <StatsBar
                        :stats="{
                            pass: reports.total.pass,
                            fail: reports.total.fail,
                        }"
                        :stats-before="null"
                        :statistics="statistics"
                    />
                </div>

                <div
                    class="mt-[22px] grid grid-cols-1 gap-[22px] lg:grid-cols-2"
                >
                    <div
                        class="rounded-[12.8px] border border-gray-200 bg-white p-6"
                    >
                        <h3 class="mb-4 text-sm font-semibold text-gray-900">
                            {{ $t("vue.dashboard_security_recommendations") }}
                        </h3>
                        <div class="space-y-4">
                            <div
                                class="flex items-start gap-4 rounded-lg border border-green-100 bg-green-50 p-4"
                            >
                                <div
                                    class="mt-2 h-2 w-2 shrink-0 rounded-full bg-green-500"
                                ></div>
                                <div>
                                    <p class="mb-1 font-medium text-green-900">
                                        {{
                                            $t(
                                                "vue.dashboard_security_recommendations_all_looking_good",
                                            )
                                        }}
                                    </p>
                                    <p class="text-sm text-green-800">
                                        {{
                                            $t(
                                                "vue.dashboard_security_recommendations_we_have",
                                            )
                                        }}
                                    </p>
                                </div>
                            </div>
                            <div
                                class="flex items-start gap-4 rounded-lg border border-blue-100 bg-blue-50 p-4"
                            >
                                <div
                                    class="mt-2 h-2 w-2 shrink-0 rounded-full bg-blue-500"
                                ></div>
                                <div>
                                    <p class="mb-1 font-medium text-blue-900">
                                        {{ $t("vue.main_sender") }}
                                    </p>
                                    <p class="text-sm text-blue-800">
                                        <template
                                            v-if="
                                                mainSender &&
                                                mainSenderPercent !== null
                                            "
                                        >
                                            <strong>{{
                                                mainSender.source
                                            }}</strong>
                                            {{ $t("vue.service_accounts_for") }}
                                            {{ mainSenderPercent.toFixed(1) }}%
                                            {{ $t("vue.of_your_email_volume") }}
                                        </template>
                                        <template v-else>
                                            {{
                                                $t(
                                                    "vue.main_sender_summary_empty",
                                                )
                                            }}
                                        </template>
                                    </p>
                                </div>
                            </div>
                            <div
                                v-if="mainWarning"
                                class="flex items-start gap-4 rounded-lg border border-red-100 bg-red-50 p-4"
                            >
                                <div
                                    class="mt-2 h-2 w-2 shrink-0 rounded-full bg-red-500"
                                ></div>
                                <div>
                                    <p class="mb-1 font-medium text-red-900">
                                        {{ $t("vue.security_alert") }}
                                    </p>
                                    <p class="text-sm text-red-800">
                                        <strong>{{
                                            mainWarning.source
                                        }}</strong>
                                        {{
                                            $t(
                                                "vue.sender_showing_low_compliance",
                                            )
                                        }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        class="rounded-[12.8px] border border-gray-200 bg-white p-6"
                    >
                        <h3 class="mb-4 text-sm font-semibold text-gray-900">
                            {{ $t("vue.security_status") }}
                        </h3>
                        <div class="space-y-4">
                            <div class="flex items-center justify-between py-2">
                                <span class="text-gray-600">{{
                                    $t("vue.dmarc_policy")
                                }}</span>
                                <span :class="policyBadgeClass">
                                    {{ policyLabel }}
                                </span>
                            </div>
                            <div class="flex items-center justify-between py-2">
                                <span class="text-gray-600">{{
                                    $t("vue.spf_record")
                                }}</span>
                                <span :class="spfBadgeClass">
                                    {{ spfBadgeLabel }}
                                </span>
                            </div>
                            <div class="flex items-center justify-between py-2">
                                <span class="text-gray-600">{{
                                    $t("vue.dkim_signing")
                                }}</span>
                                <span :class="dkimBadgeClass">
                                    {{ dkimBadgeLabel }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="mt-[22px]">
                    <StatsChart
                        v-if="reports.total.pass > 0 || reports.total.fail > 0"
                        :chart-data="reports.chartData"
                        :header="$t('vue.daily_email_reports')"
                    ></StatsChart>
                </div>

                <DynamicContentTable
                    :title="$t('vue.sender_details')"
                    :subtitle="$t('vue.authentication_compliance_metrics')"
                    :rows="tableRows"
                    :columns="tableColumns"
                    :endpoint="
                        () =>
                            route('reports.show', {
                                domain: reportedDomain.domain,
                            })
                    "
                    :base-query="tableBaseQuery"
                    :table="table"
                    :pagination="pagination"
                    row-key="source"
                    :row-link="
                        (row) =>
                            route('reports.show.source', {
                                domain: reportedDomain.domain,
                                source: row.source,
                            })
                    "
                    :search-placeholder="t('vue.search_sender')"
                    :empty-text="t('vue.no_results')"
                    :per-page-label="t('vue.per_page')"
                >
                    <template #row="{ row }">
                        <td class="px-6 py-4">
                            <div class="flex items-center gap-3">
                                <div
                                    class="inline-block h-4 w-4 rounded-sm bg-gray-100 text-center align-middle"
                                >
                                    <div
                                        class="flex h-full w-full items-center justify-center"
                                    >
                                        <img
                                            :src="resolveSourceIcon(row.source)"
                                            :alt="row.source"
                                        />
                                    </div>
                                </div>
                                <span
                                    class="font-medium text-blue-600 transition-colors duration-150"
                                >
                                    {{ row.source }}
                                </span>
                            </div>
                        </td>
                        <td class="px-6 py-4">
                            <svg
                                v-if="
                                    calculateCompliancePercent(
                                        row.pass,
                                        row.fail,
                                    ) > 80
                                "
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-circle-check-big h-4 w-4 text-green-500"
                                aria-hidden="true"
                            >
                                <path
                                    d="M21.801 10A10 10 0 1 1 17 3.335"
                                ></path>
                                <path d="m9 11 3 3L22 4"></path>
                            </svg>
                            <svg
                                v-else-if="
                                    calculateCompliancePercent(
                                        row.pass,
                                        row.fail,
                                    ) > 50
                                "
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-circle-alert h-4 w-4 text-yellow-500"
                                aria-hidden="true"
                            >
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" x2="12" y1="8" y2="12"></line>
                                <line x1="12" x2="12.01" y1="16" y2="16"></line>
                            </svg>
                            <svg
                                v-else
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-circle-x h-4 w-4 text-red-500"
                                aria-hidden="true"
                            >
                                <circle cx="12" cy="12" r="10"></circle>
                                <path d="m15 9-6 6"></path>
                                <path d="m9 9 6 6"></path>
                            </svg>
                        </td>
                        <td class="px-6 py-4">
                            <span class="text-gray-900">{{
                                new Intl.NumberFormat().format(row.sum)
                            }}</span>
                        </td>
                        <td class="px-6 py-4">
                            <div class="flex items-center gap-3">
                                <div class="w-20">
                                    <div
                                        class="h-2 w-full rounded-full bg-gray-200"
                                    >
                                        <div
                                            class="h-2 rounded-full"
                                            :style="{
                                                width:
                                                    calculateCompliancePercent(
                                                        row.pass,
                                                        row.fail,
                                                    ) + '%',
                                                backgroundColor:
                                                    calculateColorByPercents(
                                                        calculateCompliance(
                                                            row.pass,
                                                            row.fail,
                                                        ),
                                                    ),
                                            }"
                                        ></div>
                                    </div>
                                </div>
                                <span
                                    class="text-sm font-medium"
                                    :style="{
                                        color: calculateColorByPercents(
                                            calculateCompliance(
                                                row.pass,
                                                row.fail,
                                            ),
                                        ),
                                    }"
                                >
                                    {{
                                        calculateCompliance(row.pass, row.fail)
                                    }}
                                </span>
                            </div>
                        </td>
                        <td class="px-6 py-4">
                            <span class="text-gray-900">{{
                                calculateCompliance(
                                    row.spf_valid,
                                    row.spf_invalid,
                                )
                            }}</span>
                        </td>
                        <td class="px-6 py-4">
                            <span class="text-gray-900">{{
                                calculateCompliance(
                                    row.dkim_valid,
                                    row.dkim_invalid,
                                )
                            }}</span>
                        </td>
                    </template>
                </DynamicContentTable>
            </template>
        </div>
    </div>

    <UpgradeRequiredModal
        :opened="showUpgradeModal"
        title="Scale Plan Required"
        description="Custom date range selection requires a Scale plan subscription. Upgrade your subscription to access advanced filtering features."
        @close="closeUpgradeModal"
    />
</template>

<script lang="ts" setup>
import { computed, type PropType } from "vue";
import route from "ziggy-js";
import { Globe, Settings } from "lucide-vue-next";
import type TabItem from "@/types/Settings/TabItem";
import { useTranslations } from "@/composables/useTranslations.ts";
import DynamicContentTable from "@/components/Content/DynamicContentTable.vue";
import UpgradeRequiredModal from "@/components/Atomic/Modals/UpgradeRequiredModal.vue";
import useDateRangeAccess from "@/composables/useDateRangeAccess";
import useReportsAccess from "@/composables/useReportsAccess";
import BasicTabs from "@/components/Atomic/Tabs/BasicTabs.vue";
import DateRangeField from "@/Pages/Reports/components/DateRangeField.vue";
import StatsBar from "@/Pages/Reports/components/StatsBar.vue";
import StatsChart from "@/Pages/Reports/components/StatsChart.vue";
import InfoCard from "@/components/Content/InfoCard.vue";

const { t } = useTranslations();

const dataNotFound = new URL(
    "/resources/js/assets/images/search.svg",
    import.meta.url,
).href;

const { showUpgradeModal, closeUpgradeModal } = useDateRangeAccess();

const { hasReportsAccess } = useReportsAccess();

const trialEndedImg = new URL(
    "/resources/js/assets/images/plan-calendar.svg",
    import.meta.url,
).href;

interface ReportSource {
    source: string;
    pass: number;
    fail: number;
    sum: number;
    spf_valid: number;
    spf_invalid: number;
    dkim_valid: number;
    dkim_invalid: number;
}

interface ReportsData {
    total: {
        pass: number;
        fail: number;
        sum: number;
    };
    sources: Record<string, ReportSource>;
    chartData: Array<{
        day: string;
        pass: number;
        fail: number;
    }>;
}

interface ReportedDomain {
    domain: string;
    dmarcPolicy: string;
    active: boolean;
    dmarcRecord?: string | null;
}

type Statistics = {
    activeDomains: number;
    totalEmailsReported: number;
    dmarcCompliant: number;
    dmarcNonCompliant: number;
    complianceRate: number;
};

type TableState = {
    q?: string;
    sort_by?: string | null;
    sort_dir?: "asc" | "desc" | null;
    per_page?: number;
    current_page?: number;
};

type Pagination = {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
    from: number | null;
    to: number | null;
};

const props = defineProps({
    statistics: {
        type: Object as PropType<Statistics>,
        required: true,
    },
    reports: {
        type: Object as PropType<ReportsData>,
        required: true,
    },
    reportedDomain: {
        type: Object as PropType<ReportedDomain>,
        required: true,
    },
    dateRange: {
        type: Object as PropType<{
            start: string;
            end: string;
        }>,
        required: true,
    },
    tableRows: {
        type: Array as PropType<ReportSource[]>,
        required: true,
    },
    table: {
        type: Object as PropType<TableState>,
        required: true,
    },
    pagination: {
        type: Object as PropType<Pagination>,
        required: true,
    },
});

const tableColumns = computed(() => [
    {
        key: "source",
        label: t("vue.sender"),
        sortable: true,
        sortKey: "source",
        colClass: "w-[28%]",
    },
    {
        key: "health",
        label: t("vue.health"),
        sortable: false,
        colClass: "w-[10%]",
    },
    {
        key: "emails",
        label: t("vue.emails_reported"),
        sortable: true,
        sortKey: "emails",
        colClass: "w-[17%]",
    },
    {
        key: "dmarc",
        label: t("vue.dmarc_compliance"),
        sortable: true,
        sortKey: "dmarc",
        colClass: "w-[20%]",
    },
    {
        key: "spf",
        label: t("vue.spf_aligned"),
        sortable: true,
        sortKey: "spf",
        colClass: "w-[12%]",
    },
    {
        key: "dkim",
        label: t("vue.dkim_aligned"),
        sortable: true,
        sortKey: "dkim",
        colClass: "w-[13%]",
    },
]);

const tableBaseQuery = computed(() => ({
    start_date: props.dateRange.start,
    end_date: props.dateRange.end,
}));

const sourcesList = computed(() => Object.values(props.reports.sources || {}));

const sortedSources = computed(() =>
    [...sourcesList.value].sort((a, b) => (b.sum || 0) - (a.sum || 0)),
);

const mainSender = computed(() => sortedSources.value[0]);

const mainSenderPercent = computed(() => {
    if (!mainSender.value) {
        return null;
    }

    const total =
        props.reports.total.sum ||
        props.reports.total.pass + props.reports.total.fail;

    if (total === 0) {
        return null;
    }

    return Math.round((mainSender.value.sum / total) * 1000) / 10;
});

const mainWarning = computed(() =>
    sortedSources.value.find(
        (row) => calculateCompliancePercent(row.pass, row.fail) < 80,
    ),
);

const policyLabel = computed(() => {
    const policyRaw = (props.reportedDomain.dmarcPolicy || "").toLowerCase();

    if (policyRaw === "reject") return t("vue.reject");
    if (policyRaw === "quarantine") return t("vue.quarantine");
    if (policyRaw === "none" || policyRaw === "") return t("vue.none");

    return t("vue.none");
});

const policyBadgeClass = computed(() => {
    const base = "policy-badge";
    const policyRaw = (props.reportedDomain.dmarcPolicy || "").toLowerCase();

    if (policyRaw === "reject") return `${base} policy-reject`;
    if (policyRaw === "quarantine") return `${base} policy-quarantine`;

    return `${base} policy-none`;
});

const spfTotals = computed(() => {
    return sourcesList.value.reduce(
        (acc, row) => {
            acc.valid += row.spf_valid || 0;
            acc.invalid += row.spf_invalid || 0;
            return acc;
        },
        { valid: 0, invalid: 0 },
    );
});

const dkimTotals = computed(() => {
    return sourcesList.value.reduce(
        (acc, row) => {
            acc.valid += row.dkim_valid || 0;
            acc.invalid += row.dkim_invalid || 0;
            return acc;
        },
        { valid: 0, invalid: 0 },
    );
});

const getAuthBadge = (valid: number, invalid: number) => {
    const base = "policy-badge";
    const total = valid + invalid;

    if (total === 0) {
        return {
            label: t("vue.inactive"),
            className: `${base} badge-inactive`,
        };
    }

    return {
        label: t("vue.active"),
        className: `${base} badge-active`,
    };
};

const spfBadge = computed(() =>
    getAuthBadge(spfTotals.value.valid, spfTotals.value.invalid),
);

const dkimBadge = computed(() =>
    getAuthBadge(dkimTotals.value.valid, dkimTotals.value.invalid),
);

const spfBadgeLabel = computed(() => spfBadge.value.label);
const spfBadgeClass = computed(() => spfBadge.value.className);
const dkimBadgeLabel = computed(() => dkimBadge.value.label);
const dkimBadgeClass = computed(() => dkimBadge.value.className);

const calculateCompliancePercent = (pass: number, fail: number) => {
    const total = pass + fail;
    if (total === 0) return 0;
    return Math.round((pass / total) * 100);
};

const calculateCompliance = (pass: number, fail: number) => {
    return pass + fail > 0
        ? Math.floor((pass / (pass + fail)) * 100) + "%"
        : "0%";
};

const calculateColorByPercents = (percents: string) => {
    const percentNum = parseInt(percents.replace("%", ""), 10);

    if (percentNum > 75) {
        return "#15803D";
    }

    if (percentNum > 50) {
        return "#CA8A04";
    }

    return "#B91C1C";
};

const resolveSourceIcon = (source: string) => {
    if (source === "Ecomail") {
        return "https://ecomail.cz/images/favicons/favicon-32x32.png";
    }

    return `https://www.google.com/s2/favicons?domain=${source}&sz=40`;
};

const tabs: TabItem[] = [
    {
        title: t("vue.reports"),
        key: "domain-reports",
        icon: Globe,
        url: route("reports.show", { domain: props.reportedDomain.domain }),
    },
    {
        title: t("vue.settings"),
        key: "domain-settings",
        icon: Settings,
        url: route("reports.settings.show", {
            domain: props.reportedDomain.domain,
        }),
    },
];
</script>
